import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TableSortLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Print as PrintIcon,
} from '@mui/icons-material';

const CompanyPartnerList = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [partners, setPartners] = useState([]);
  const [filteredPartners, setFilteredPartners] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [error, setError] = useState(null);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('companyName');

  // Filter state for each column
  const [filters, setFilters] = useState({
    companyName: '',
    foreignAgent: '',
    country: '',
    isActive: '',
  });

  useEffect(() => {
    fetchPartners();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [partners, filters]);

  const fetchPartners = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get('/CompanyPartners');
      console.log('Partners data:', response.data);
      setPartners(response.data || []);
    } catch (error) {
      console.error('Error fetching partners:', error);
      setError('Failed to load partners. Please try again.');
      setPartners([]);
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...partners];

    // Filter by company name - starts with
    if (filters.companyName) {
      const search = filters.companyName.toLowerCase();
      filtered = filtered.filter((partner) => {
        const companyName = (partner.companyName || '').toLowerCase();
        return companyName.startsWith(search);
      });
    }

    // Filter by foreign agent - starts with
    if (filters.foreignAgent) {
      const search = filters.foreignAgent.toLowerCase();
      filtered = filtered.filter((partner) => {
        const agentName = (partner.foreignAgentName || '').toLowerCase();
        return agentName.startsWith(search);
      });
    }

    // Filter by country - starts with
    if (filters.country) {
      const search = filters.country.toLowerCase();
      filtered = filtered.filter((partner) => {
        const country = (partner.foreignAgentCountry || '').toLowerCase();
        return country.startsWith(search);
      });
    }

    // Filter by active status
    if (filters.isActive !== '') {
      const isActive = filters.isActive === 'true';
      filtered = filtered.filter((partner) => partner.isActive === isActive);
    }

    setFilteredPartners(filtered);
    setPage(0);
  };

  const handleFilterChange = (field) => (event) => {
    setFilters((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAddNew = () => {
    navigate('/partners/create');
  };

  const handleEdit = (id) => {
    navigate(`/partners/edit/${id}`);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this partner?')) {
      try {
        await api.delete(`/CompanyPartners/${id}`);
        fetchPartners();
      } catch (error) {
        console.error('Error deleting partner:', error);
        setError('Failed to delete partner. Please try again.');
      }
    }
  };

  const handleRefresh = () => {
    fetchPartners();
    setFilters({
      companyName: '',
      foreignAgent: '',
      country: '',
      isActive: '',
    });
    setOrder('asc');
    setOrderBy('companyName');
  };

  const handlePrint = () => {
    window.print();
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Sort function
  const sortData = (data, orderBy, order) => {
    return [...data].sort((a, b) => {
      let aValue = a[orderBy] || '';
      let bValue = b[orderBy] || '';

      // Handle string comparison
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      // Handle boolean comparison
      if (typeof aValue === 'boolean') {
        aValue = aValue ? 1 : 0;
        bValue = bValue ? 1 : 0;
      }

      if (order === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return bValue < aValue ? -1 : bValue > aValue ? 1 : 0;
      }
    });
  };

  // Apply sorting to filtered data
  const sortedData = sortData(filteredPartners, orderBy, order);

  // Pagination
  const paginatedPartners = sortedData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Render filter row
  const renderFilterRow = () => (
    <TableRow sx={{ bgcolor: '#fafafa' }}>
      <TableCell sx={{ p: 1 }}>
        <TextField
          size="small"
          placeholder="Filter company"
          value={filters.companyName}
          onChange={handleFilterChange('companyName')}
          fullWidth
          variant="outlined"
        />
      </TableCell>
      <TableCell sx={{ p: 1 }}>
        <TextField
          size="small"
          placeholder="Filter agent"
          value={filters.foreignAgent}
          onChange={handleFilterChange('foreignAgent')}
          fullWidth
          variant="outlined"
        />
      </TableCell>
      <TableCell sx={{ p: 1 }}>
        <TextField
          size="small"
          placeholder="Filter country"
          value={filters.country}
          onChange={handleFilterChange('country')}
          fullWidth
          variant="outlined"
        />
      </TableCell>
      <TableCell sx={{ p: 1, width: '150px' }}>
        <FormControl fullWidth size="small">
          <InputLabel>Status</InputLabel>
          <Select
            value={filters.isActive}
            onChange={handleFilterChange('isActive')}
            label="Status"
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="true">Active</MenuItem>
            <MenuItem value="false">Inactive</MenuItem>
          </Select>
        </FormControl>
      </TableCell>
      <TableCell sx={{ p: 1 }} align="center">
        <Tooltip title="Clear Filters">
          <IconButton
            size="small"
            onClick={() => setFilters({
              companyName: '',
              foreignAgent: '',
              country: '',
              isActive: '',
            })}
          >
            ✕
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );

  return (
    <Box sx={{ p: 3, maxHeight: 'calc(100vh - 100px)', overflow: 'auto' }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" component="h1">
            Company Partners
          </Typography>
          <Box>
            <Tooltip title="Refresh">
              <IconButton onClick={handleRefresh} color="primary">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Print">
              <IconButton onClick={handlePrint} color="primary">
                <PrintIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddNew}
              sx={{ ml: 1 }}
            >
              New Partner
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Table */}
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="300px">
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      <TableSortLabel
                        active={orderBy === 'companyName'}
                        direction={orderBy === 'companyName' ? order : 'asc'}
                        onClick={() => handleSort('companyName')}
                      >
                        Company
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      <TableSortLabel
                        active={orderBy === 'foreignAgentName'}
                        direction={orderBy === 'foreignAgentName' ? order : 'asc'}
                        onClick={() => handleSort('foreignAgentName')}
                      >
                        Foreign Agent
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      <TableSortLabel
                        active={orderBy === 'foreignAgentCountry'}
                        direction={orderBy === 'foreignAgentCountry' ? order : 'asc'}
                        onClick={() => handleSort('foreignAgentCountry')}
                      >
                        Country
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="center">
                      <TableSortLabel
                        active={orderBy === 'isActive'}
                        direction={orderBy === 'isActive' ? order : 'asc'}
                        onClick={() => handleSort('isActive')}
                      >
                        Status
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="center">
                      Actions
                    </TableCell>
                  </TableRow>
                  {/* Filter Row */}
                  {renderFilterRow()}
                </TableHead>
                <TableBody>
                  {paginatedPartners.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography variant="body1" color="textSecondary" sx={{ py: 3 }}>
                          No partners found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedPartners.map((partner) => (
                      <TableRow key={partner.partnerID} hover>
                        <TableCell>{partner.companyName || 'N/A'}</TableCell>
                        <TableCell>{partner.foreignAgentName || 'N/A'}</TableCell>
                        <TableCell>
                          <Chip 
                            label={partner.foreignAgentCountry || 'N/A'} 
                            size="small" 
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={partner.isActive ? 'Active' : 'Inactive'}
                            color={partner.isActive ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEdit(partner.partnerID)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(partner.partnerID)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              component="div"
              count={filteredPartners.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              labelRowsPerPage="Rows per page:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
            />
          </>
        )}
      </Paper>
    </Box>
  );
};

export default CompanyPartnerList;